import { Component, DestroyRef, inject, OnDestroy, OnInit } from '@angular/core';
import { FeatureFlags, QueueTypes } from '@core/enumeration';
import { AuthenticationService, QueueService } from '@core/service';
import { FeatureToggleService, SecurityService } from '@mynexus/mynexus-ui-lib';
import { ToastMessageOptions, MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { queueByGroup } from './config';
import { UserConfigService } from '@core/service/user-config.service';
import { Router } from '@angular/router';
import { PortalProducts } from '@shared/enumeration';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface QueueConfig {
  name: QueueTypes;
  access: string[];
  condition?: boolean;
}

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.scss'],
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly queueService = inject(QueueService);
  private readonly messageService = inject(MessageService);
  private readonly securityService = inject(SecurityService);
  private readonly userConfigService = inject(UserConfigService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly featureToggleService = inject(FeatureToggleService);

  selectedQueueType: QueueTypes = null;
  errorMessage: ToastMessageOptions | null = null;
  isAdminPanelExternalWelcomeContentEnabled = false;

  readonly featureFlags = FeatureFlags;
  readonly queueTypes = QueueTypes;

  readonly isExternalUser = this.authenticationService.isExternalUser;

  readonly showReferralWelcomePage = this.authenticationService.isSdohUserExternal
    || this.authenticationService.isReferralProgramProviderAdmin
    || this.authenticationService.isWCCExternal
    || this.securityService.hasAccess([
      'SDoHReferralDashboard.Pending.Read',
      'SDoHReferralDashboard.My.Read',
      'SDoHReferralDashboard.All.Read',
      'SDoHReferralDashboard.Active.Read',
    ]);

  private destroy$ = new Subject<void>();

  constructor(private readonly router: Router){}

  ngOnInit() {
    if (this.isExternalUser) {
      this.getUserData();
    }

    if (!this.isExternalUser) {
      this.setQueueType();
    }

    this.queueService.activeQueueType
      .pipe(takeUntil(this.destroy$))
      .subscribe((type: QueueTypes) => this.selectedQueueType = type);

    this.securityService.getErrorLoadedPermissionAPI$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.errorMessage = {
          summary: 'Error',
          detail: 'An error occurred. Please try again later or contact the Carelon team at (844) 411-9622.',
        };
      });

    this.featureToggleService.getFeatureAccess(FeatureFlags.AdminPanelExternalWelcomeContent)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.isAdminPanelExternalWelcomeContentEnabled = result;
      });

    this.setQueueType();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUserData() {
    this.authenticationService.getUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe((user) => {
        if (!user.facsimileTelephoneNumber) {
          this.messageService.add({
            key: 'faxIssue',
            severity: 'info',
            summary: 'No Fax number!',
            detail: `Please contact the Carelon Portal Support Team to update your phone and fax number.
            You will not be able to request authorizations until this contact information is updated.
            You can reach Portal Support at 844-411-9622, option 4. Thank you.`,
            sticky: true,
          });
        }
      });
  }

  public navigateToHHAuth(event: Event): void {
    const rowData = (event as CustomEvent<any>)?.detail;

    this.router.navigate(
      [rowData.tenantId, 'home-health', 'auth-dashboard', rowData.authorizationNumber]
    );
  }

  public navigateToPACAuth(event: Event): void {
    const { authorizationNumber, tenantId } = (event as CustomEvent<any>)?.detail || { };
    const productName = this.selectedQueueType === QueueTypes.PACQueue ? PortalProducts.PACIM : PortalProducts.PacCommercial;

    this.router.navigate(
      ['/', tenantId, productName, 'auth-dashboard', authorizationNumber]
    );
  }

  public navigateToMemberPage(event: any, targetWidget?: string): void {
    const { tenantId, referralId, memberId, subscriberNumber, referralNumber } = event.detail || { };

    if (referralId) {
      sessionStorage.setItem('selected-referral-id', referralId);
    }

    this.router.navigate([tenantId, 'member', subscriberNumber], {
      state: {
        targetWidget,
        tenantId,
        referralId,
        memberId,
        expandReferral: !!(referralId && targetWidget === 'wound-management'),
      },
    });
  }

  /**
   * Set the active queue based on the user's group
   * @private
   */
  private setQueueType(): void {
    const savedQueue = this.userConfigService.getUserPrioritizedQueue();
    const userQueues = this.getUserQueues();

    // Save list of queues to the queue service
    // This is necessary to reduce amount of props passed to the queue-action-buttons component
    this.queueService.queueTypes.next(userQueues);

    if (savedQueue && userQueues.includes(savedQueue)) {
      this.queueService.setActiveQueueType(savedQueue);

      return;
    }

    // Fallback to the default queue by user's group in case nothing yet saved in userConfig
    const defaultGroup = Object.keys(queueByGroup).find((group) => this.authenticationService.hasGroup(group));
    const defaultQueueByGroup = queueByGroup[defaultGroup];

    this.queueService.setActiveQueueType(defaultQueueByGroup || QueueTypes.HHQueue);
  }

  private getUserQueues(): QueueTypes[] {
    if (!this.securityService.hasAnyPermissions()) {
      return [];
    }

    // Note! Queues should be sorted appropriately by name manually
    const queuesConfig: QueueConfig[] = [
      {
        name: QueueTypes.AGQueue,
        access: ['AppealGrievanceQueue.Read'],
      },
      {
        name: QueueTypes.DMEPOS_MCD,
        access: ['DMEPOSMedicaidQueue.Read'],
      },
      {
        name: QueueTypes.DMEPOS_MA,
        access: ['DMEPOSAuthorizationCaseQueue.Read'],
      },
      {
        name: QueueTypes.HHQueue,
        access: ['HHServiceLineQueue.Read'],
      },
      {
        name: QueueTypes.PACCommercialQueue,
        access: ['PACCOMServiceLineQueue.Read'],
      },
      {
        name: QueueTypes.PACQueue,
        access: ['PACMAServiceLineQueue.Read'],
      },
      {
        name: QueueTypes.SdohQueue,
        access: ['SDoHQueue.Read'],
      },
      {
        name: QueueTypes.WCCAdmissionsQueue,
        access: ['WoundCareAdmissionQueue.Read'],
      },
    ];

    return queuesConfig
      .filter(({ access }) => this.securityService.hasAccess(access))
      .map(({ name }) => name);
  }
}

